import {useEffect, useState} from "react";
import BarChart from "../Components/BarChart";
import Table from "../Components/Table";
import ActionButton from "../Components/ActionButton";
import {Client} from "../Api/Client";
import Accordion from "../Components/Accordion";
import Spinner from "../Components/Spinner";
import {useParams} from "react-router-dom";
import MultiLineChart from "../Components/MultiLineChart";
import {mapRoutesEnToSv} from "../Utils/routes";

function GraphSection({content, id, onLoad}: any) {
    const params = useParams();

    const [isLoading, setIsLoading] = useState(false);
    const [sort, setSort] = useState('count');
    const [sortBy, setSortBy] = useState('desc');
    const [sectionData, setSectionData] = useState<any>([]);
    const [lineChartData, setLineChartData] = useState<any>([]);
    const [checkedItems, setCheckedItems] = useState([]);

    const defaultColors = ['#0072C2', '#D12960', '#E23D0E', '#A26400', '#578900', '#0B998A', '#7B47FF', '#BF2FCA'];

    const exclude = params.type ?? '';

    let total = content.tRows ? content.tRows : sectionData.length;
    total = total > 8 ? 8 : total;

    async function getSectionData (sort: string, sortBy: string, signal: any) {
        setIsLoading(true);
        Client.get(content.endpoint, { params: { sort: sort, sort_by: sortBy }, signal: signal })
            .then((response) => {
                let items = response.data.data.table?.filter((item: any) => item.type !== exclude);
                if (content.tRows !== null) {
                    items = items?.slice(0, content.tRows)
                }
                setSectionData(items ?? []);

                if (content.chart === 'line') {
                    const chartItems = response.data.data.chart;
                    chartItems.datasets = response.data.data.chart?.datasets?.filter((item: any) => item.type !== exclude);
                    setLineChartData(chartItems);
                }
                onLoad();
                setTimeout(() => setIsLoading(false), 250);
            })
            .catch((e) => {
                console.log(e.message)
            })
    }

    useEffect(() => {
        const abortController = new AbortController();
        getSectionData(sort, sortBy, abortController.signal)

        return () => abortController.abort();
    }, [sort, sortBy, content.endpoint, exclude])

    useEffect(() => {
        setCheckedItems(sectionData.map((entry: any, index: number) => {
            return {
                type: entry.type,
                checked: index < 5,
                active: total > 5 ? true : index < total,
                color: index < total ? defaultColors[index] : null,
            }
        }));
    }, [sectionData]);

    function onItemClickHandler(itemType: any) {
        setCheckedItems((prevCheckedItems: any) => {
            let newCheckedItems = prevCheckedItems.map((item: any) => {
                return {
                    ...item,
                    checked: item.type === itemType ? !item.checked : item.checked,
                };
            });

            const isMaxItemsChecked = newCheckedItems.filter((item: any) => item.checked).length === total;
            newCheckedItems = newCheckedItems.map((entry: any, index: number) => {
                return {
                    ...entry,
                    active: isMaxItemsChecked ? entry.checked : true,
                    color: entry.checked ? entry.color : null,
                }
            })

            const usedColors = newCheckedItems.map((entry: any) => entry.color).filter((color: any) => !!color);
            let differenceColors = defaultColors.filter(x => !usedColors.includes(x));

            if (differenceColors.length) {
                newCheckedItems = newCheckedItems.map((entry: any) => {
                    return {
                        ...entry,
                        color: entry.checked && !entry.color ? differenceColors.shift() : entry.color,
                    }
                })
            }

            return newCheckedItems;
        });
    }

    function onSortChangeHandler(sort: string, sortBy: string) {
        setSort(sort);
        setSortBy(sortBy);
    }

    return (
        <div className="relative" id={id}>
            {isLoading && <Spinner />}
            <Accordion icon={content.icon} title={content.title} description={content.text}>
                {sectionData?.length === 0
                    ? <p className="font-body">There is no such data yet.</p>
                    : <>
                        <div className="xl:flex xl:gap-6">
                            <div className="xl:flex-1 bg-grey-900">
                                {content.chart === 'bar'
                                    ? <BarChart data={sectionData} checkedItems={checkedItems} total={total} />
                                    : <MultiLineChart data={lineChartData} checkedItems={checkedItems} total={total} />
                                }
                            </div>
                            <div className="my-2.5 xl:mt-0 xl:flex-1 border border-grey-700 rounded-md ps-2.5 pb-2.5 max-h-[55vh] xl:max-h-[70vh] overflow-y-auto overflow-x-hidden table-scroller">
                                <Table
                                    data={sectionData}
                                    onItemClick={onItemClickHandler}
                                    checkedItems={checkedItems}
                                    onSortChange={onSortChangeHandler}
                                    sort={sort}
                                    sortBy={sortBy}
                                    total={total}
                                    head={content.tHead}
                                />
                            </div>
                        </div>
                        {content.tRows && content.tRows <= sectionData.length &&
                            <ActionButton href={mapRoutesEnToSv(content.endpoint)} state={{ prevPath: window.location.pathname + '#' + id }}>
                                {content.viewAllText ?? 'Visa fler'}
                            </ActionButton>
                        }
                    </>
                }
            </Accordion>
        </div>
    );
}

export default GraphSection;